import React, {useCallback, useContext, useMemo, useState} from 'react';
import {RadioButton, Separator} from '@priority-software/priority-style-react';
import {AutomationContext} from '../../../../../context';
import {priority} from '@priority-software/priority-proto-api/lib';
import {DropdownItemData} from '@priority-software/priority-style-react/dist/components/DropdownV2/ChooseDropdown/ChooseDropdown';
import {
    getBPMStatusCondition,
    prepareTimeConditionOptions,
    BPMStatusCondition as BPMStatusConditionValue
} from './utils';
import {RadioContent} from './parts/RadioContent';

type Props = {};

export const BPMStatusCondition = (props: Props) => {
    const {openRule, choosesOptions, updateOpenRule} = useContext(AutomationContext);
    const {TimeUnits, DateFields} = choosesOptions;

    const {BPM} = openRule;
    const {TimeValue, TimeUnit, TimeField} = BPM || {};
    const [radioAction, setRadioAction] = useState(getBPMStatusCondition(BPM));
    const [timeCondition, setTimeCondition] = useState<number>((TimeValue as number) < 0 ? -1 : 1);

    const timeConditionOptions = useMemo(prepareTimeConditionOptions, []);

    const onChange = useCallback(
        (data: string | number, name?: string) => {
            updateOpenRule({
                BPM: {
                    ...BPM,
                    [name]: data
                }
            });
        },
        [updateOpenRule, BPM]
    );

    const onRadioChange = useCallback(
        (value: number) => {
            setRadioAction(value);

            let newBPMData: priority.netitems.form.IAlertBPM = {
                ...BPM,
                TimeValue: 0,
                TimeField: null,
                TimeUnit: null
            };

            if (value === 2) {
                newBPMData = {
                    ...BPM,
                    TimeUnit: TimeUnits[0]?.value as number,
                    TimeValue: 0
                };
            } else if (value === 3) {
                newBPMData = {
                    ...BPM,
                    TimeField: DateFields[0]?.value as number,
                    TimeUnit: TimeUnits[0]?.value as number,
                    TimeValue: 0
                };
            }

            setTimeCondition(1);
            updateOpenRule({
                BPM: newBPMData
            });
        },
        [updateOpenRule, BPM, DateFields, TimeUnits]
    );

    const onChangeTimeValue = useCallback(
        (value: number) => {
            const valueToSet = timeCondition * Math.abs(value);
            onChange(valueToSet, 'TimeValue');
        },
        [onChange, timeCondition]
    );

    const onDropdownChange = useCallback(
        (option: DropdownItemData, isSelected: boolean, name?: string) => {
            onChange(option.value as number, name);
        },
        [onChange]
    );

    const onTimeStatusConditionChange = useCallback(
        (option: DropdownItemData) => {
            setTimeCondition(option.value as number);
            updateOpenRule({
                BPM: {
                    ...BPM,
                    TimeValue: (option.value as number) * Math.abs(BPM.TimeValue as number) || 0
                }
            });
        },
        [BPM, updateOpenRule]
    );

    const radioOptions = [
        {
            label: (
                <RadioContent
                    isActive={BPMStatusConditionValue.Default === radioAction}
                    type={BPMStatusConditionValue.Default}
                />
            ),
            value: BPMStatusConditionValue.Default
        },
        {
            label: (
                <RadioContent
                    isActive={BPMStatusConditionValue.TimeValue === radioAction}
                    type={BPMStatusConditionValue.TimeValue}
                    onDropdownChange={onDropdownChange}
                    onChangeTimeValue={onChangeTimeValue}
                    timeUnits={TimeUnits}
                    timeValue={Math.abs(TimeValue as number) || 0}
                    timeUnit={TimeUnit as number}
                />
            ),
            value: BPMStatusConditionValue.TimeValue
        },
        {
            label: (
                <RadioContent
                    isActive={BPMStatusConditionValue.TimeField === radioAction}
                    timeConditionOptions={timeConditionOptions}
                    timeCondition={timeCondition}
                    type={BPMStatusConditionValue.TimeField}
                    onDropdownChange={onDropdownChange}
                    onChangeTimeValue={onChangeTimeValue}
                    onTimeStatusConditionChange={onTimeStatusConditionChange}
                    timeUnits={TimeUnits}
                    dateFields={DateFields}
                    timeField={TimeField as number}
                    timeUnit={TimeUnit as number}
                    timeValue={Math.abs(TimeValue as number) || 0}
                />
            ),
            value: BPMStatusConditionValue.TimeField
        }
    ];

    return (
        <>
            <div
                style={{
                    backgroundColor: 'var(--primary-color-100, #F5F9FF)',
                    padding: 8,
                    borderRadius: 4
                }}
            >
                <RadioButton
                    onChange={onRadioChange}
                    selected={radioAction}
                    border={false}
                    isHorizontal={false}
                    options={radioOptions}
                />
            </div>

            <Separator
                height={1}
                align="vertical"
                style={{margin: '16px 0', backgroundColor: 'var(--primary-color-200, #E3EEFF)'}}
            />
        </>
    );
};
